import * as React from "react";
import { useLocation } from "react-router";
import { usagePatternFor, USAGE_ACTIONS } from "@cm/domain/usage";
import { request } from "./api";

/**
 * Registro de uso do produto (métricas do sistema no Master).
 * Envia ao NOSSO servidor: tela aberta pelo padrão da rota (/app/leads/:id — sem IDs), tempo ativo na tela
 * (aba visível e interação no último minuto) e algumas ações de interface. Sem conteúdo, filtros ou textos.
 */
type Panel = "app" | "admin";
type Ev = { kind: "view" | "time" | "action"; name: string; active_ms?: number; tab_id: string };

const IDLE_MS = 60_000;
const TICK_MS = 5_000;
const FLUSH_MS = 30_000;
const tabId = Math.random().toString(36).slice(2, 12);
const queues: Record<Panel, Ev[]> = { app: [], admin: [] };
let lastInteraction = Date.now();
let currentPanel: Panel | null = null;
// Evita contar duas vezes a mesma abertura (efeitos duplicados do React em desenvolvimento, re-render da rota).
let lastView: { name: string; at: number } | null = null;

function flush(panel: Panel, keepalive = false) {
  const q = queues[panel];
  while (q.length) {
    const batch = q.splice(0, 50);
    void request("POST", `/api/${panel}/usage`, { events: batch }, { keepalive }).catch(() => undefined);
  }
}

/** Ação de interface do catálogo (ex.: "ai.panel_open"). Ignorada fora do painel do cliente. */
export function trackAction(name: keyof typeof USAGE_ACTIONS | string) {
  if (currentPanel !== "app" || !(name in USAGE_ACTIONS)) return;
  queues.app.push({ kind: "action", name, tab_id: tabId });
}

export function useUsageTracking(panel: Panel | null) {
  const loc = useLocation();
  const screen = React.useRef<{ name: string; active: number } | null>(null);

  const closeScreen = React.useCallback(() => {
    const cur = screen.current;
    if (panel && cur && cur.active >= 1000) queues[panel].push({ kind: "time", name: cur.name, active_ms: Math.min(Math.round(cur.active), 3_600_000), tab_id: tabId });
    if (cur) cur.active = 0;
  }, [panel]);

  // Tela nova a cada mudança de rota (pelo padrão; caminhos fora do catálogo não contam).
  React.useEffect(() => {
    if (!panel) return;
    currentPanel = panel;
    closeScreen();
    const name = usagePatternFor(loc.pathname);
    screen.current = name ? { name, active: 0 } : null;
    const now = Date.now();
    if (name && !(lastView && lastView.name === name && now - lastView.at < 1500)) queues[panel].push({ kind: "view", name, tab_id: tabId });
    if (name) lastView = { name, at: now };
  }, [loc.pathname, panel, closeScreen]);

  React.useEffect(() => {
    if (!panel) return;
    const touch = () => {
      lastInteraction = Date.now();
    };
    const evs = ["pointerdown", "keydown", "wheel", "touchstart", "mousemove"] as const;
    for (const e of evs) window.addEventListener(e, touch, { passive: true });
    let last = Date.now();
    const tick = window.setInterval(() => {
      const now = Date.now();
      if (screen.current && document.visibilityState === "visible" && now - lastInteraction < IDLE_MS) screen.current.active += now - last;
      last = now;
    }, TICK_MS);
    const periodic = window.setInterval(() => {
      closeScreen();
      flush(panel);
    }, FLUSH_MS);
    const onHide = () => {
      if (document.visibilityState === "hidden") {
        closeScreen();
        flush(panel, true);
      } else {
        last = Date.now();
      }
    };
    const onPageHide = () => {
      closeScreen();
      flush(panel, true);
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      for (const e of evs) window.removeEventListener(e, touch);
      window.clearInterval(tick);
      window.clearInterval(periodic);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      closeScreen();
      flush(panel);
      if (currentPanel === panel) currentPanel = null;
    };
  }, [panel, closeScreen]);
}
